import { z } from "zod";

import { statusSchema } from "@/schemas/status";

const boostStateSchema = z.enum([
  // Boost admitted at purchase but held until the status's media (video)
  // finishes encoding; dispatched to moderation once ready. Must be listed here
  // even before the pill/theme entries — the enum has no `.catch()`, so an
  // unknown state fails the whole boost and `filteredArray` silently drops it
  // from My Boosts.
  "pending_media",
  "pending_moderation",
  "pending_payment",
  "active",
  "completed",
  "rejected",
  "canceled",
  "payment_failed",
]);

const boostCompletionReasonSchema = z.enum([
  "expired",
  "impression_cap_reached",
  "user_canceled",
  "source_deleted",
  "payment_failure",
  // System cancels for a pending_media boost: media failed to encode, the wait
  // timed out, or the account was suspended during the wait.
  "media_processing_failed",
  "media_processing_timeout",
  "account_suspended",
]);

const boostModerationStatusSchema = z.enum([
  "pending",
  "approved",
  "moderation_rejected",
]);

const boostSchema = z
  .object({
    // The API serializes the public UUID under `id` (REST::BoostSerializer#id ->
    // external_boost_id), not `uuid`. Reading `uuid` here left it undefined and
    // failed the whole boost in Zod, so every boost was silently dropped and
    // "My Boosts" was always empty. Read `id` and expose it as `uuid` below.
    id: z.string(),
    state: boostStateSchema,
    // Dollars, as scale-pinned strings ("9.99") — REST::BoostSerializer
    // renders both with format('%.2f', ...) over numeric(8, 2) columns.
    // Same convention as subscription-payment.ts; see boost-eligibility.ts
    // for why these are coerced to numbers at the boundary.
    amount: z.coerce.number(),
    tax_amount: z.coerce.number(),
    impressions_delivered: z.number().default(0),
    impression_cap: z.number(),
    duration_hours: z.number().default(24),
    activated_at: z.string().nullable().catch(null),
    // Validate as ISO 8601 so countdown math in `TimeRemaining` never sees
    // a NaN-producing string. Matches the nullable-timestamp pattern used
    // by `status.edited_at` and `group.deleted_at`.
    expires_at: z.string().datetime().nullable().catch(null),
    completed_at: z.string().nullable().catch(null),
    completion_reason: boostCompletionReasonSchema.nullable().catch(null),
    moderation_status: boostModerationStatusSchema,
    moderation_rejected_reason: z.string().nullable().catch(null),
    // Treat a malformed `status` payload as null rather than failing the
    // whole boost, but make the drift observable so we notice when the
    // server starts sending an unexpected shape. Without this, a row
    // silently renders an "unknown status" placeholder. See review 012.
    status: statusSchema.nullable().catch((ctx) => {
      if (
        typeof process !== "undefined" &&
        process.env?.NODE_ENV !== "production"
      ) {
        // eslint-disable-next-line no-console
        console.warn("[boost] status schema parse failed", ctx.error);
      }
      return null;
    }),
    created_at: z.string(),
  })
  .transform((input) => ({
    ...input,
    uuid: input.id,
  }));

type Boost = z.infer<typeof boostSchema>;
type BoostState = z.infer<typeof boostStateSchema>;
type BoostCompletionReason = z.infer<typeof boostCompletionReasonSchema>;
type BoostModerationStatus = z.infer<typeof boostModerationStatusSchema>;

export {
  boostSchema,
  boostStateSchema,
  boostCompletionReasonSchema,
  boostModerationStatusSchema,
  type Boost,
  type BoostState,
  type BoostCompletionReason,
  type BoostModerationStatus,
};
